import React from 'react';
import { FaBook, FaSeedling } from 'react-icons/fa';
import { ConditionalsPanel } from './ConditionalsPanel';
import { AttachmentsPanel } from './AttachmentsPanel';
import { ContractStepCompensation } from './ContractStepCompensation';
import { ContractPreviewPane } from './ContractPreviewPane';
import { renderField, renderBlock } from './ContractFieldRenderers';
import {
  PreviewResponse,
  SchemaField,
  BlockSchemaField,
  Template,
  AttachmentTemplate,
  AttachmentSelection,
  ConditionalFieldPresence,
} from './useContractWizard';
import css from './ContractWizardModal.module.css';

export interface ContractStepDetailsProps {
  loadingPreview: boolean;
  loadingTemplates: boolean;
  selectedEmploymentType: string;
  selectedEmploymentTypeLabel: string;
  startDate: string;
  selectedWA: number | null;
  selectedWAName: string;
  selectedPositionKey: string;
  selectedPositionLabel: string;
  preview: PreviewResponse | null;
  selectedTemplateId: string;
  selectedSuggestionIdx: number;
  setSelectedSuggestionIdx: (idx: number) => void;
  setSelectedTemplateId: (id: string) => void;
  setActiveStep: (step: number) => void;
  patchProfile: boolean;
  setPatchProfile: (val: boolean) => void;
  prefillsApplied: boolean;
  resetPrefills: () => void;
  handlePreviewPdf: () => void;
  loadingPdfPreview: boolean;
  pdfUrl: string;
  previewSrc: string | Blob | undefined;
  compensationType: 'hourly' | 'salary';
  setCompensationType: (type: 'hourly' | 'salary') => void;
  formValues: Record<string, any>;
  handleFieldChange: (key: string, value: any) => void;
  validationErrors: Record<string, string>;
  minijobWarning: string;
  filteredRequiredFields: SchemaField[];
  filteredOptionalFields: SchemaField[];
  visibleOptionalFieldKeys: Set<string>;
  setVisibleOptionalFieldKeys: React.Dispatch<React.SetStateAction<Set<string>>>;
  presence: ConditionalFieldPresence;
  collapsedBlocks: Record<string, boolean>;
  setCollapsedBlocks: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  blockValues: Record<string, string>;
  handleBlockChange: (blockKey: string, value: string) => void;
  isAdmin: boolean;
  isManager: boolean;
  attachments: AttachmentTemplate[];
  attachmentSelections: AttachmentSelection[];
  setAttachmentSelections: (selections: AttachmentSelection[]) => void;
  attachmentValidationErrors: Record<string, string>;
  makeCoverPage: boolean;
  setMakeCoverPage: (val: boolean) => void;
  makeCombinedPdf: boolean;
  setMakeCombinedPdf: (val: boolean) => void;
  allTemplates: Template[];
  handleManualTemplateSelect: (key: string) => void;
  handleNavigateToLibrary: () => void;
  handleSeedTemplates: () => void;
  seeding: boolean;
  onOpenAnnexModal: () => void;
  hourlyRate: number | null;
  setHourlyRate: (val: number | null) => void;
  salaryMonthlyCents: number | null;
  setSalaryMonthlyCents: (val: number | null) => void;
  contractEndDate: string;
  setContractEndDate: (val: string) => void;
  probationMonths: number | null;
  setProbationMonths: (val: number | null) => void;
  extraLeaveDays: number | null;
  setExtraLeaveDays: (val: number | null) => void;
}

export function ContractStepDetails(props: ContractStepDetailsProps) {
  const {
    loadingPreview, loadingTemplates, selectedEmploymentType, selectedEmploymentTypeLabel,
    startDate, selectedWA, selectedWAName, selectedPositionKey, selectedPositionLabel,
    preview, selectedTemplateId, selectedSuggestionIdx, setSelectedSuggestionIdx,
    setSelectedTemplateId, setActiveStep, patchProfile, setPatchProfile, prefillsApplied,
    resetPrefills, handlePreviewPdf, loadingPdfPreview, pdfUrl, previewSrc, compensationType,
    setCompensationType, formValues, handleFieldChange, validationErrors, minijobWarning,
    filteredRequiredFields, filteredOptionalFields, visibleOptionalFieldKeys,
    setVisibleOptionalFieldKeys, presence, collapsedBlocks, setCollapsedBlocks, blockValues,
    handleBlockChange, isAdmin, isManager, attachments, attachmentSelections,
    setAttachmentSelections, attachmentValidationErrors, makeCoverPage, setMakeCoverPage,
    makeCombinedPdf, setMakeCombinedPdf, allTemplates, handleManualTemplateSelect,
    handleNavigateToLibrary, handleSeedTemplates, seeding, onOpenAnnexModal, hourlyRate,
    setHourlyRate, salaryMonthlyCents, setSalaryMonthlyCents, contractEndDate,
    setContractEndDate, probationMonths, setProbationMonths, extraLeaveDays, setExtraLeaveDays,
  } = props;

  if (loadingPreview || loadingTemplates) {
    return <div className={css.step1Loading}><div>{loadingPreview ? 'Loading preview...' : 'Loading templates...'}</div></div>;
  }

  return (
    <div className={css.step1Content}>
      <div className={css.step0Header}>
        <h3 className={css.sectionHeading}>Vertragsdetails</h3>
        <button onClick={onOpenAnnexModal} className={css.createOtherBtn}>Create other...</button>
      </div>

      <SummaryBar {...{ selectedEmploymentTypeLabel, startDate, selectedWA, selectedWAName, selectedPositionKey, selectedPositionLabel, setActiveStep }} />

      {prefillsApplied && (
        <div className={css.prefillsBanner}>
          <div className={css.prefillsText}><span className={css.prefillsIcon}>✅</span><span>Vorschläge aus Position angewendet</span></div>
          <button type="button" onClick={resetPrefills} className={css.prefillsResetBtn}>Zurücksetzen</button>
        </div>
      )}

      <TemplateSection {...{ preview, selectedTemplateId, selectedSuggestionIdx, setSelectedSuggestionIdx, setSelectedTemplateId, handlePreviewPdf, loadingPdfPreview, pdfUrl, allTemplates, handleManualTemplateSelect, handleNavigateToLibrary, handleSeedTemplates, seeding, isAdmin, selectedWA, selectedPositionKey, selectedEmploymentType }} />

      <div>
        <label className={css.checkboxRow}>
          <input type="checkbox" checked={patchProfile} onChange={(e) => setPatchProfile(e.target.checked)} style={{ cursor: 'pointer' }} />
          <span>Update employee profile with these values</span>
        </label>
      </div>

      {minijobWarning && <div className={css.warningBox}>{minijobWarning}</div>}

      {selectedEmploymentType === 'MINIJOB' && preview?.schema && (
        <div className={css.minijobAutoInfo}>
          <p className={css.minijobAutoTitle}>{'💡 Minijob Auto-Berechnung'}</p>
          <ul className={css.minijobAutoList}>
            <li><strong>monthly_hours</strong> wird automatisch berechnet: 556 € / hourly_rate</li>
            <li><strong>contract_hours_per_week</strong> wird automatisch berechnet: (monatliche Stunden × 12 / 52)</li>
            <li>Beide Werte werden beim Submit automatisch mitgeschickt</li>
            <li>Du wirst gewarnt, wenn die Berechnung die 556 € Grenze überschreitet</li>
          </ul>
        </div>
      )}

      {(presence.hasFixedTerm || presence.hasOtherEmployment) && (
        <ConditionalsPanel
          values={{ is_fixed_term: formValues['is_fixed_term'] || false, contract_end_date: formValues['contract_end_date'], fixed_term_reason: formValues['fixed_term_reason'], has_other_employment: formValues['has_other_employment'] || false, other_employment_employer: formValues['other_employment_employer'], other_employment_street: formValues['other_employment_street'], other_employment_house_no: formValues['other_employment_house_no'], other_employment_postal_code: formValues['other_employment_postal_code'], other_employment_city: formValues['other_employment_city'], other_employment_start_date: formValues['other_employment_start_date'], other_employment_monthly_salary: formValues['other_employment_monthly_salary'] }}
          onChange={(patch) => { Object.entries(patch).forEach(([key, value]) => { handleFieldChange(key, value); }); }}
          presence={presence} errors={validationErrors}
        />
      )}

      {preview?.schema && (
        <>
          <ContractStepCompensation {...{ selectedEmploymentType, compensationType, setCompensationType, formValues, handleFieldChange, validationErrors, minijobWarning }} />

          {filteredRequiredFields.length > 0 && (
            <><h3 className={css.fieldSectionHeading}>Required Fields</h3>
            {filteredRequiredFields.map((field) => renderField(field, true, formValues, validationErrors, handleFieldChange, selectedEmploymentType, collapsedBlocks, setCollapsedBlocks, patchProfile))}</>
          )}

          {filteredOptionalFields.filter((f) => visibleOptionalFieldKeys.has(f.key)).length > 0 && (
            <><h3 className={css.fieldSectionHeading}>Optional Fields</h3>
            {filteredOptionalFields.filter((f) => visibleOptionalFieldKeys.has(f.key)).map((field) => renderField(field, false, formValues, validationErrors, handleFieldChange, selectedEmploymentType, collapsedBlocks, setCollapsedBlocks, patchProfile))}</>
          )}

          {filteredOptionalFields.filter((f) => !visibleOptionalFieldKeys.has(f.key)).length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <button type="button" onClick={() => { const avail = filteredOptionalFields.filter((f) => !visibleOptionalFieldKeys.has(f.key)); if (avail.length > 0) setVisibleOptionalFieldKeys((prev) => new Set([...Array.from(prev), avail[0].key])); }} className={css.addOptionalBtn}>
                <span className={css.addOptionalIcon}>+</span>
                Optionales Feld hinzufügen ({filteredOptionalFields.filter((f) => !visibleOptionalFieldKeys.has(f.key)).length} verfügbar)
              </button>
              <select value="" onChange={(e) => { if (e.target.value) setVisibleOptionalFieldKeys((prev) => new Set([...Array.from(prev), e.target.value])); }} className={css.addOptionalSelect}>
                <option value="">-- Feld auswählen --</option>
                {filteredOptionalFields.filter((f) => !visibleOptionalFieldKeys.has(f.key)).map((field) => (<option key={field.key} value={field.key}>{field.label}</option>))}
              </select>
            </div>
          )}
        </>
      )}

      {preview?.blocks && preview.blocks.length > 0 && (
        <><h3 className={css.fieldSectionHeading}>Editable Blocks</h3>
        <div className={css.blocksInfoBox}>{'💡 Customize contract clauses and sections below. Only editable blocks for your role are shown.'}</div>
        {preview.blocks.map((block) => renderBlock(block, blockValues, handleBlockChange, isAdmin, isManager))}</>
      )}

      <ContractPreviewPane previewSrc={previewSrc} onRefresh={handlePreviewPdf} loadingPdfPreview={loadingPdfPreview} />

      {attachments.length > 0 && (
        <>
          <AttachmentsPanel attachments={attachments} selections={attachmentSelections} onSelectionsChange={setAttachmentSelections} validationErrors={attachmentValidationErrors} />
          {attachmentSelections.length > 0 && <div className={css.attachmentInfoBox}>{'ℹ️ Anhänge und das Deckblatt sind in der Vorschau nicht sichtbar. Nach Erstellung sehen Sie alles im kombinierten PDF im Dokument-Drawer.'}</div>}
          {attachmentSelections.length > 0 && (
            <div className={css.bundleOptionsBox}>
              <h4 className={css.bundleOptionsTitle}>Bundle-Optionen</h4>
              <div className={css.bundleOptionsGroup}>
                <label className={css.bundleCheckboxLabel}><input type="checkbox" checked={makeCoverPage} onChange={(e) => setMakeCoverPage(e.target.checked)} className={css.bundleCheckbox} />Cover Page erzeugen</label>
                <label className={css.bundleCheckboxLabel}><input type="checkbox" checked={makeCombinedPdf} onChange={(e) => setMakeCombinedPdf(e.target.checked)} className={css.bundleCheckbox} />PDFs kombinieren</label>
              </div>
              {!makeCombinedPdf && <div className={css.bundleWarning}>{'⚠️ Hinweis: Ohne PDF-Kombination werden die Dokumente einzeln erstellt.'}</div>}
            </div>
          )}
        </>
      )}

      {!preview?.schema && <NoSchemaFallback {...{ contractEndDate, setContractEndDate, probationMonths, setProbationMonths, extraLeaveDays, setExtraLeaveDays, compensationType, setCompensationType, hourlyRate, setHourlyRate, salaryMonthlyCents, setSalaryMonthlyCents }} />}
    </div>
  );
}

function SummaryBar({ selectedEmploymentTypeLabel, startDate, selectedWA, selectedWAName, selectedPositionKey, selectedPositionLabel, setActiveStep }: { selectedEmploymentTypeLabel: string; startDate: string; selectedWA: number | null; selectedWAName: string; selectedPositionKey: string; selectedPositionLabel: string; setActiveStep: (step: number) => void }) {
  return (
    <div className={css.summaryBar}>
      <div className={css.summaryText}>
        <strong>Beschäftigungsart:</strong> {selectedEmploymentTypeLabel}{' \u2022 '}<strong>Beginn:</strong> {startDate || 'Nicht gesetzt'}
        {selectedWA && <>{' \u2022 '}<strong>Work Area:</strong> {selectedWAName}</>}
        {selectedPositionKey && selectedPositionKey !== null && <>{' \u2022 '}<strong>Position:</strong> {selectedPositionLabel}</>}
      </div>
      <button type="button" onClick={() => setActiveStep(0)} className={css.summaryChangeBtn}>Ändern</button>
    </div>
  );
}

function TemplateSection({ preview, selectedTemplateId, selectedSuggestionIdx, setSelectedSuggestionIdx, setSelectedTemplateId, handlePreviewPdf, loadingPdfPreview, pdfUrl, allTemplates, handleManualTemplateSelect, handleNavigateToLibrary, handleSeedTemplates, seeding, isAdmin, selectedWA, selectedPositionKey, selectedEmploymentType }: { preview: PreviewResponse | null; selectedTemplateId: string; selectedSuggestionIdx: number; setSelectedSuggestionIdx: (idx: number) => void; setSelectedTemplateId: (id: string) => void; handlePreviewPdf: () => void; loadingPdfPreview: boolean; pdfUrl: string; allTemplates: Template[]; handleManualTemplateSelect: (key: string) => void; handleNavigateToLibrary: () => void; handleSeedTemplates: () => void; seeding: boolean; isAdmin: boolean; selectedWA: number | null; selectedPositionKey: string; selectedEmploymentType: string }) {
  if (!preview) return null;

  if (preview.suggestions.length > 0) {
    return (
      <div>
        <label className={css.labelStyle}>Template auswählen</label>
        <select value={`${preview.suggestions[selectedSuggestionIdx]?.template_id}:${preview.suggestions[selectedSuggestionIdx]?.is_any_binding ? 'any' : 'spec'}:${selectedSuggestionIdx}`} onChange={(e) => { const parts = e.target.value.split(':'); setSelectedSuggestionIdx(Number(parts[2])); setSelectedTemplateId(parts[0]); }} className={`${css.inputStyle} ${css.templateSelect}`}>
          {preview.suggestions.map((sug, idx) => { const key = `${sug.template_id}:${sug.is_any_binding ? 'any' : 'spec'}:${idx}`; const scoreLabel = sug.score > 0 ? ' \u2022 BEST MATCH' : sug.is_any_binding ? ' \u2022 FALLBACK' : ''; return (<option key={key} value={key}>{sug.template_id} (Score: {sug.score.toFixed(2)}){scoreLabel} – {sug.explanation}</option>); })}
        </select>
        {selectedTemplateId && preview.suggestions[selectedSuggestionIdx] && <div className={css.templateExplanation}>{preview.suggestions[selectedSuggestionIdx]?.explanation}</div>}
        <div className={css.previewActions}>
          <button onClick={handlePreviewPdf} disabled={loadingPdfPreview || !selectedTemplateId} className={css.pdfPreviewBtn}>{loadingPdfPreview ? 'Generiere...' : 'PDF Vorschau'}</button>
          {pdfUrl && <a href={pdfUrl} target="_blank" rel="noreferrer" className={css.pdfOpenLink}>Vorschau öffnen</a>}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className={css.noTemplatesHeading}>No Matching Templates Found</h3>
      <div className={css.noTemplatesWarning}><p className={css.noTemplatesWarningText}>No templates match the selected Work Area ({selectedWA}), Position ({selectedPositionKey}), and Employment Type ({selectedEmploymentType}).</p></div>
      <div className={css.ctaCards}>
        <div onClick={handleNavigateToLibrary} className={css.ctaCardLibrary}>
          <div className={css.ctaCardIcon}><FaBook size={20} color="#3b82f6" /><h4 className={css.ctaCardTitle} style={{ color: '#3b82f6' }}>Add from Library</h4></div>
          <p className={css.ctaCardDesc}>Browse templates and create bindings for this combination</p>
        </div>
        {isAdmin && (
          <div onClick={handleSeedTemplates} className={seeding ? css.ctaCardSeedDisabled : css.ctaCardSeed}>
            <div className={css.ctaCardIcon}><FaSeedling size={20} color="#22c55e" /><h4 className={css.ctaCardTitle} style={{ color: '#22c55e' }}>{seeding ? 'Seeding...' : 'Import Seed Pack'}</h4></div>
            <p className={css.ctaCardDesc}>Import standard templates (service_cafe_de_basic)</p>
          </div>
        )}
      </div>
      {allTemplates.length > 0 && (
        <div>
          <h4 className={css.manualSelectHeading}>Or select manually:</h4>
          <select value={selectedTemplateId} onChange={(e) => handleManualTemplateSelect(e.target.value)} className={css.inputStyle}>
            <option value="">Choose a template...</option>
            {allTemplates.map((t) => (<option key={t.template_key} value={t.template_key}>{t.name} ({t.template_key})</option>))}
          </select>
        </div>
      )}
    </div>
  );
}

function NoSchemaFallback({ contractEndDate, setContractEndDate, probationMonths, setProbationMonths, extraLeaveDays, setExtraLeaveDays, compensationType, setCompensationType, hourlyRate, setHourlyRate, salaryMonthlyCents, setSalaryMonthlyCents }: { contractEndDate: string; setContractEndDate: (v: string) => void; probationMonths: number | null; setProbationMonths: (v: number | null) => void; extraLeaveDays: number | null; setExtraLeaveDays: (v: number | null) => void; compensationType: 'hourly' | 'salary'; setCompensationType: (t: 'hourly' | 'salary') => void; hourlyRate: number | null; setHourlyRate: (v: number | null) => void; salaryMonthlyCents: number | null; setSalaryMonthlyCents: (v: number | null) => void }) {
  return (
    <>
      <div><label className={css.labelStyle}>Contract End Date (optional)</label><input type="date" value={contractEndDate} onChange={(e) => setContractEndDate(e.target.value)} className={css.inputStyle} /></div>
      <div><label className={css.labelStyle}>Probation Period (months)</label><input type="number" value={probationMonths ?? ''} onChange={(e) => setProbationMonths(e.target.value ? Number(e.target.value) : null)} className={css.inputStyle} placeholder="e.g., 6" /></div>
      <div><label className={css.labelStyle}>Extra Leave Days per Year</label><input type="number" value={extraLeaveDays ?? ''} onChange={(e) => setExtraLeaveDays(e.target.value ? Number(e.target.value) : null)} className={css.inputStyle} placeholder="e.g., 2" /></div>
      <h3 className={css.compensationHeading}>Compensation</h3>
      <div><label className={css.labelStyle}>Compensation Type</label><select value={compensationType} onChange={(e) => setCompensationType(e.target.value as 'hourly' | 'salary')} className={css.inputStyle}><option value="SALARY">Salary (Monthly)</option><option value="HOURLY">Hourly Rate</option></select></div>
      {compensationType === 'hourly' && (<div><label className={css.labelStyle}>Hourly Rate (€)</label><input type="number" step="0.01" value={hourlyRate ?? ''} onChange={(e) => setHourlyRate(e.target.value ? Number(e.target.value) : null)} className={css.inputStyle} placeholder="e.g., 15.50" /></div>)}
      {compensationType === 'salary' && (<div><label className={css.labelStyle}>Monthly Salary (cents)</label><input type="number" value={salaryMonthlyCents ?? ''} onChange={(e) => setSalaryMonthlyCents(e.target.value ? Number(e.target.value) : null)} className={css.inputStyle} placeholder="e.g., 300000" /><div className={css.centHint}>Enter amount in cents (e.g., 300000 for €3000)</div></div>)}
    </>
  );
}
